import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { AccountLinkPreview, AccountLinkProvider, RedeemAccountLinkResponse } from "@rivets/core";
import { BrandLogo } from "@/components/BrandLogo";
import { useAccountLinkPreview, useRedeemAccountLink } from "@/lib/api";
import { autoLinkVerificationText } from "@/features/settings/AutoLinkVerificationNote";

/**
 * `/link/:token` — the one destination of every account-link challenge (Slack,
 * Teams, Linear).
 *
 * Opening it links nothing (security review 01 H1, 03 H-4, 04 H2). The page
 * reads what the challenge would bind — which provider user, in which
 * workspace, to which Rivets account — and waits for an explicit Confirm,
 * because the person holding the link is not necessarily the person who was
 * sent it. Confirming binds the identity only: the message that produced the
 * challenge was not run, and the sender is asked to send it again.
 */
export function AccountLinkPage() {
  const { token = "" } = useParams({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const preview = useAccountLinkPreview(token);
  const redeem = useRedeemAccountLink();
  const [linked, setLinked] = useState<RedeemAccountLinkResponse | undefined>(undefined);

  if (linked) {
    return (
      <AccountLinkShell tone="success" title={`${providerName(linked.provider)} account linked`}>
        <p className="auth-subtitle" style={{ margin: "0 auto 20px", maxWidth: 380 }}>
          {linkedMessage(linked.provider)}
        </p>
      </AccountLinkShell>
    );
  }
  if (!/^[0-9a-f]{64}$/i.test(token)) {
    return (
      <AccountLinkShell tone="error" title="This link can't be used">
        <p className="auth-subtitle" style={{ margin: "0 auto", maxWidth: 360 }}>This account-link request is invalid.</p>
      </AccountLinkShell>
    );
  }
  if (preview.isPending) {
    return (
      <AccountLinkShell tone="pending" title="Checking this link…">
        <div className="spinner" style={{ margin: "0 auto" }} />
      </AccountLinkShell>
    );
  }
  if (preview.isError || !preview.data) {
    return (
      <AccountLinkShell tone="error" title="This link can't be used">
        <p className="auth-subtitle" style={{ margin: "0 auto", maxWidth: 360 }}>
          {preview.error instanceof Error ? preview.error.message : "Rivets could not read this account-link request."}
        </p>
      </AccountLinkShell>
    );
  }
  return (
    <AccountLinkConfirmation
      preview={preview.data.preview}
      pending={redeem.isPending}
      error={redeem.error instanceof Error ? redeem.error.message : undefined}
      onConfirm={() => redeem.mutate({ token }, { onSuccess: setLinked })}
      onCancel={() => void navigate({ to: "/" })}
    />
  );
}

/** The confirmation itself, separate from data loading so it renders in tests. */
export function AccountLinkConfirmation({ preview, pending, error, onConfirm, onCancel }: {
  preview: AccountLinkPreview;
  pending: boolean;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const copy = accountLinkCopy(preview);
  return (
    <AccountLinkShell tone="pending" title={copy.title}>
      <p className="auth-subtitle" data-testid="account-link-question" style={{ margin: "0 auto 12px", maxWidth: 380 }}>
        {copy.question}
      </p>
      {preview.external.email ? (
        <p className="hint" style={{ margin: "0 0 12px" }}>
          {providerName(preview.provider)} email: {preview.external.email}
        </p>
      ) : null}
      <div
        className={`alert ${copy.emailNote.tone === "warning" ? "alert-warning" : copy.emailNote.tone === "success" ? "alert-success" : ""}`}
        role={copy.emailNote.tone === "warning" ? "alert" : "status"}
        data-testid={`account-link-email-${preview.emailCheck}`}
        style={{ textAlign: "left", marginBottom: 16 }}
      >
        <span>{copy.emailNote.text}</span>
      </div>
      {preview.account.emailVerified ? null : (
        <p className="hint" data-testid="account-link-unverified" style={{ margin: "0 0 16px", textAlign: "left" }}>
          {autoLinkVerificationText()} You can verify it in Settings → Account.
        </p>
      )}
      {error ? <div className="error-text" style={{ marginBottom: 12 }}>{error}</div> : null}
      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        <button className="btn" type="button" onClick={onCancel} disabled={pending}>Cancel</button>
        <button
          className={`btn ${copy.emailNote.tone === "warning" ? "btn-danger" : "btn-primary"}`}
          type="button"
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? "Linking…" : copy.confirmLabel}
        </button>
      </div>
    </AccountLinkShell>
  );
}

export function providerName(provider: AccountLinkProvider): string {
  switch (provider) {
    case "slack": return "Slack";
    case "teams": return "Microsoft Teams";
    case "linear": return "Linear";
  }
}

/** Every sentence the confirmation shows, from the preview alone. */
export function accountLinkCopy(preview: AccountLinkPreview): {
  title: string;
  question: string;
  emailNote: { tone: "warning" | "success" | "neutral"; text: string };
  confirmLabel: string;
} {
  const provider = providerName(preview.provider);
  const who = preview.external.name ?? preview.external.userId;
  const workspace = preview.workspace.name ?? preview.workspace.id;
  const question = `Link ${provider} user @${who} (${workspace} workspace) to your Rivets account `
    + `${preview.account.email} in ${preview.organization.name}?`;
  if (preview.emailCheck === "mismatch") {
    return {
      title: `Link ${provider} account?`,
      question,
      emailNote: {
        tone: "warning",
        text: `The ${provider} account's verified email (${preview.external.email}) is different from your `
          + `verified Rivets email (${preview.account.email}). Only continue if this ${provider} account is yours — `
          + "whoever uses it will act as you in Rivets.",
      },
      confirmLabel: "Link anyway",
    };
  }
  return {
    title: `Link ${provider} account?`,
    question,
    emailNote: preview.emailCheck === "match"
      ? { tone: "success", text: "Same verified email on both accounts." }
      : {
          tone: "neutral",
          text: `Rivets can't confirm this is your ${provider} account. Only continue if you just messaged Rivets from it — `
            + "whoever uses it will act as you in Rivets.",
        },
    confirmLabel: "Link account",
  };
}

export function linkedMessage(provider: AccountLinkProvider): string {
  const name = providerName(provider);
  return `Your ${name} account is linked. Rivets didn't run your earlier message — send it again in ${name}`
    + " and Rivets will pick it up. You can close this tab.";
}

function AccountLinkShell({ tone, title, children }: {
  tone: "pending" | "success" | "error";
  title: string;
  children: React.ReactNode;
}) {
  return (
    <main className="auth-shell">
      <section className="auth-card" aria-live="polite">
        <BrandLogo className="auth-logo" size={22} />
        <div className="panel" style={{ textAlign: "center", padding: 24 }}>
          {tone !== "pending" ? (
            <div
              aria-hidden="true"
              style={{
                width: 38, height: 38, margin: "0 auto 16px", borderRadius: "50%",
                display: "grid", placeItems: "center",
                border: `1px solid ${tone === "success" ? "var(--success)" : "var(--danger)"}`,
                color: tone === "success" ? "var(--success)" : "var(--danger)", fontSize: "var(--glyph-xl)",
              }}
            >
              {tone === "success" ? "✓" : "!"}
            </div>
          ) : null}
          <h1 className="auth-title" style={{ marginBottom: 8 }}>{title}</h1>
          {children}
        </div>
      </section>
    </main>
  );
}
